import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  PageHeader,
  TabView,
  ResourceLayout,
  Button,
  Alert,
  Paper,
  Text,
  Group,
  Badge,
  Menu,
  Modal,
  ProcurementMobileListRow,
  formatAddress,
  type ResourceQuery,
} from '@vipso/shared';
import { useSelector } from 'react-redux';
import {
  useListProcurementQuery,
  useExportProcurementMutation,
  useLazyGetCustomerQuery,
  useLazyGetVendorQuery,
  useLazyGetProductQuery,
  type ProcRow,
} from '../api/tenantApi';
import type { RootState } from '../store';
import { downloadOrdersImportTemplate, importOrdersExcel } from '../api/importOrders';
import { useDate } from '../hooks/useDate';
import { useAccess } from '../hooks/useAccess';
import { useNavAccess } from '../hooks/useNavAccess';
import { useApprovalModuleAccess } from '../hooks/useApprovalModuleAccess';
import { NoAccessState } from '../components/RequireFeature';
import { useModuleTerminology } from '../hooks/useModuleLabel';
import { CreateOrderWizard } from '../features/procurement/CreateOrderWizard';
import { ProcurementEntityFilters } from '../features/procurement/ProcurementEntityFilters';
import {
  setProcFilterIds,
  countProcEntityFilters,
  hasProcEntityFilters,
  readProcEntityFiltersFromHash,
  readProcEntityFiltersFromParams,
  filterProcRowsByEntities,
  type ProcEntityFilterIds,
} from '../features/procurement/procurementFilters';

function procPartyName(row: ProcRow, untitled: string): string {
  return row.customer?.name || row.vendor?.name || row.ordNumber || untitled;
}

function procProductSummary(
  row: ProcRow,
  formatSummary: (name: string, qty: number) => string,
): { summary?: string; moreCount: number } {
  const products = row.products ?? [];
  if (!products.length) return { moreCount: 0 };
  const first = products[0];
  const name = first?.product?.name;
  const qty = first?.quantity;
  if (!name) return { moreCount: Math.max(0, products.length - 1) };
  return {
    summary: formatSummary(name, qty ?? 0),
    moreCount: Math.max(0, products.length - 1),
  };
}

function procAddress(row: ProcRow): string | undefined {
  const formatted =
    formatAddress(row.customer?.address) || formatAddress(row.vendor?.address);
  return formatted || undefined;
}

function approvalBadgeColor(status?: string | null) {
  switch (status) {
    case 'APPROVED':
      return 'green';
    case 'REJECTED':
      return 'red';
    case 'PENDING':
      return 'yellow';
    default:
      return 'gray';
  }
}

export function ProcurementPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canMutate } = useAccess();
  const nav = useNavAccess();
  const approvalAccess = useApprovalModuleAccess();
  const customerTerms = useModuleTerminology('customer');
  const vendorTerms = useModuleTerminology('vendor');
  const productTerms = useModuleTerminology('product');
  const [searchParams, setSearchParams] = useSearchParams();

  const filterOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string }> = [];
    if (nav.salesOrders && nav.purchaseOrders) {
      opts.push({ value: 'all', label: t('orders.filterAll') });
    }
    if (nav.salesOrders) {
      opts.push({ value: 'sales', label: t('orders.filterSales') });
    }
    if (nav.purchaseOrders) {
      opts.push({ value: 'purchase', label: t('orders.filterPurchase') });
    }
    return opts;
  }, [t, nav.salesOrders, nav.purchaseOrders]);

  const defaultTab = filterOptions[0]?.value ?? 'sales';
  const canCreateSales = canMutate('procurement.sales.create');
  const canCreatePurchase = canMutate('procurement.purchase.create');

  const [activeEntityFilters, setActiveEntityFilters] = useState<ProcEntityFilterIds>(() =>
    readProcEntityFiltersFromParams(searchParams),
  );

  useEffect(() => {
    setActiveEntityFilters(readProcEntityFiltersFromParams(searchParams));
  }, [searchParams]);

  const { customerIds, vendorIds, productIds } = activeEntityFilters;
  const entityFilterCount = countProcEntityFilters(customerIds, vendorIds, productIds);
  const hasEntityFilter = hasProcEntityFilters(customerIds, vendorIds, productIds);
  const entityFilterKey = `${customerIds.join(',')}|${vendorIds.join(',')}|${productIds.join(',')}`;

  const [customerLabels, setCustomerLabels] = useState<Record<string, string>>({});
  const [vendorLabels, setVendorLabels] = useState<Record<string, string>>({});
  const [productLabels, setProductLabels] = useState<Record<string, string>>({});

  const [fetchCustomer] = useLazyGetCustomerQuery();
  const [fetchVendor] = useLazyGetVendorQuery();
  const [fetchProduct] = useLazyGetProductQuery();

  useEffect(() => {
    for (const id of customerIds) {
      void fetchCustomer(id)
        .unwrap()
        .then((res) => {
          const name = res.getCustomer?.name;
          if (!name) return;
          setCustomerLabels((prev) => (prev[id] ? prev : { ...prev, [id]: name }));
        })
        .catch(() => undefined);
    }
  }, [customerIds.join(','), fetchCustomer]);

  useEffect(() => {
    for (const id of vendorIds) {
      void fetchVendor(id)
        .unwrap()
        .then((res) => {
          const name = res.getVendor?.name;
          if (!name) return;
          setVendorLabels((prev) => (prev[id] ? prev : { ...prev, [id]: name }));
        })
        .catch(() => undefined);
    }
  }, [vendorIds.join(','), fetchVendor]);

  useEffect(() => {
    for (const id of productIds) {
      void fetchProduct(id)
        .unwrap()
        .then((res) => {
          const name = res.getProduct?.name;
          if (!name) return;
          setProductLabels((prev) => (prev[id] ? prev : { ...prev, [id]: name }));
        })
        .catch(() => undefined);
    }
  }, [productIds.join(','), fetchProduct]);

  useEffect(() => {
    if (hasEntityFilter && nav.salesOrders && nav.purchaseOrders) {
      setTab('all');
    }
  }, [
    hasEntityFilter,
    nav.salesOrders,
    nav.purchaseOrders,
    customerIds.join(','),
    vendorIds.join(','),
    productIds.join(','),
  ]);

  const { format, timeZone } = useDate();
  const [tab, setTab] = useState(() => {
    const initial = readProcEntityFiltersFromHash();
    if (
      hasProcEntityFilters(
        initial.customerIds,
        initial.vendorIds,
        initial.productIds,
      ) &&
      nav.salesOrders &&
      nav.purchaseOrders
    ) {
      return 'all';
    }
    if (nav.salesOrders) return 'sales';
    if (nav.purchaseOrders) return 'purchase';
    return 'sales';
  });

  useEffect(() => {
    if (!filterOptions.length) return;
    if (!filterOptions.some((opt) => opt.value === tab)) {
      setTab(defaultTab);
    }
  }, [filterOptions, tab, defaultTab]);

  const [query, setQuery] = useState<ResourceQuery>({
    page: 1,
    pageLimit: 20,
    search: '',
  });
  const [wizardOpen, setWizardOpen] = useState(false);

  const listType = tab === 'sales' ? 'SALES' : tab === 'purchase' ? 'PURCHASE' : 'ALL';
  const effectiveListType = hasEntityFilter ? 'ALL' : listType;
  const canCreateActive =
    listType === 'SALES'
      ? canCreateSales
      : listType === 'PURCHASE'
        ? canCreatePurchase
        : canCreateSales || canCreatePurchase;
  const canMutateActive =
    listType === 'SALES'
      ? canMutate('procurement.sales.create') ||
        canMutate('procurement.sales.update') ||
        canMutate('procurement.sales.delete')
      : listType === 'PURCHASE'
        ? canMutate('procurement.purchase.create') ||
          canMutate('procurement.purchase.update') ||
          canMutate('procurement.purchase.delete')
        : canCreateSales ||
          canCreatePurchase ||
          canMutate('procurement.sales.update') ||
          canMutate('procurement.purchase.update') ||
          canMutate('procurement.sales.delete') ||
          canMutate('procurement.purchase.delete');

  const { data, isLoading, isFetching, isError, fulfilledTimeStamp, refetch } = useListProcurementQuery({
    listType: effectiveListType,
    page: query.page,
    pageLimit: query.pageLimit,
    customerIds: customerIds.length ? customerIds : undefined,
    vendorIds: vendorIds.length ? vendorIds : undefined,
    productIds: productIds.length ? productIds : undefined,
  });
  const [exportProcurement, { isLoading: exporting }] = useExportProcurementMutation();
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [importResult, setImportResult] = useState<{
    imported: number;
    parsed: number;
    errors: Array<{ row: number; message: string }>;
  } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const [lastAppliedFilterKey, setLastAppliedFilterKey] = useState(entityFilterKey);
  useEffect(() => {
    if (!isFetching && data) {
      setLastAppliedFilterKey(entityFilterKey);
    }
  }, [isFetching, data, entityFilterKey, fulfilledTimeStamp]);

  const rawList =
    hasEntityFilter && lastAppliedFilterKey !== entityFilterKey
      ? undefined
      : data?.listProcurement;

  const list = useMemo(() => {
    if (!rawList) return rawList;
    if (!hasEntityFilter) return rawList;
    const filteredRows = filterProcRowsByEntities(rawList.rows, activeEntityFilters);
    if (filteredRows.length === rawList.rows.length) return rawList;
    return {
      ...rawList,
      rows: filteredRows,
      total: filteredRows.length,
    };
  }, [rawList, hasEntityFilter, activeEntityFilters]);

  const updateEntityFilters = useCallback(
    (
      next: {
        customerIds?: string[];
        vendorIds?: string[];
        productIds?: string[];
      },
      labelUpdates?: {
        customerLabels?: Record<string, string>;
        vendorLabels?: Record<string, string>;
        productLabels?: Record<string, string>;
      },
    ) => {
      const params = new URLSearchParams(searchParams);
      if (next.customerIds !== undefined) {
        setProcFilterIds(params, 'customerIds', 'customerId', next.customerIds);
      }
      if (next.vendorIds !== undefined) {
        setProcFilterIds(params, 'vendorIds', 'vendorId', next.vendorIds);
      }
      if (next.productIds !== undefined) {
        setProcFilterIds(params, 'productIds', 'productId', next.productIds);
      }
      const nextCustomerIds = next.customerIds ?? customerIds;
      const nextVendorIds = next.vendorIds ?? vendorIds;
      const nextProductIds = next.productIds ?? productIds;
      setActiveEntityFilters({
        customerIds: nextCustomerIds,
        vendorIds: nextVendorIds,
        productIds: nextProductIds,
      });
      setSearchParams(params);
      setQuery((q) => ({ ...q, page: 1 }));
      if (hasProcEntityFilters(nextCustomerIds, nextVendorIds, nextProductIds)) {
        setTab('all');
      }
      if (labelUpdates?.customerLabels) {
        setCustomerLabels((prev) => ({ ...prev, ...labelUpdates.customerLabels }));
      }
      if (labelUpdates?.vendorLabels) {
        setVendorLabels((prev) => ({ ...prev, ...labelUpdates.vendorLabels }));
      }
      if (labelUpdates?.productLabels) {
        setProductLabels((prev) => ({ ...prev, ...labelUpdates.productLabels }));
      }
    },
    [searchParams, setSearchParams, customerIds, vendorIds, productIds],
  );

  function applyFilter(value: string) {
    setTab(value);
    setQuery((q) => ({ ...q, page: 1 }));
  }

  async function handleExport(format: 'EXCEL' | 'TALLY') {
    try {
      const result = await exportProcurement({ listType, format }).unwrap();
      alert(t('orders.exportQueued', { id: result.exportProcurement.jobId }));
    } catch (e: unknown) {
      const msg =
        (e as { data?: { message?: string }; message?: string })?.data?.message ??
        (e as { message?: string })?.message ??
        t('orders.exportFailed');
      alert(msg);
    }
  }

  async function handleDownloadTemplate() {
    if (!accessToken) return;
    setTemplateLoading(true);
    setImportError(null);
    try {
      await downloadOrdersImportTemplate(accessToken);
    } catch (err) {
      setImportError(err instanceof Error ? err.message : t('orders.importFailed'));
    } finally {
      setTemplateLoading(false);
    }
  }

  async function handleImportUpload() {
    if (!accessToken || !importFile) return;
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const result = await importOrdersExcel(
        importFile,
        accessToken,
        listType === 'ALL' ? undefined : listType,
      );
      setImportResult({
        imported: result.imported,
        parsed: result.parsed,
        errors: result.errors,
      });
      setImportFile(null);
      if (result.imported > 0) {
        void refetch();
      }
    } catch (err) {
      setImportError(err instanceof Error ? err.message : t('orders.importFailed'));
    } finally {
      setImporting(false);
    }
  }

  const hasApprovedSales = useMemo(
    () =>
      (data?.listProcurement.rows ?? []).some(
        (r) => r.procType === 'SALES' && r.approvalStatus === 'APPROVED',
      ),
    [data],
  );

  function clearEntityFilters() {
    const params = new URLSearchParams(searchParams);
    setProcFilterIds(params, 'customerIds', 'customerId', []);
    setProcFilterIds(params, 'vendorIds', 'vendorId', []);
    setProcFilterIds(params, 'productIds', 'productId', []);
    setActiveEntityFilters({ customerIds: [], vendorIds: [], productIds: [] });
    setSearchParams(params);
    setQuery((q) => ({ ...q, page: 1 }));
  }

  const untitled = t('orders.untitledOrder');
  const empty = t('common.emptyValue');

  const entityFilters = (
    <ProcurementEntityFilters
      customerIds={customerIds}
      vendorIds={vendorIds}
      productIds={productIds}
      customerLabels={customerLabels}
      vendorLabels={vendorLabels}
      productLabels={productLabels}
      onCustomerChange={(ids, labels) =>
        updateEntityFilters({ customerIds: ids }, { customerLabels: labels })
      }
      onVendorChange={(ids, labels) =>
        updateEntityFilters({ vendorIds: ids }, { vendorLabels: labels })
      }
      onProductChange={(ids, labels) =>
        updateEntityFilters({ productIds: ids }, { productLabels: labels })
      }
    />
  );

  const typeFilterContent = (
    <Stack gap="xs">
      <Text size="xs" c="dimmed" fw={600} tt="uppercase">
        {t('orders.orderType')}
      </Text>
      {filterOptions.map((opt) => (
        <Button
          key={opt.value}
          variant={tab === opt.value ? 'filled' : 'default'}
          onClick={() => applyFilter(opt.value)}
        >
          {opt.label}
        </Button>
      ))}
    </Stack>
  );

  const filterContent = (
    <Stack gap="md">
      {typeFilterContent}
      {entityFilters}
      {hasEntityFilter ? (
        <Button variant="light" color="gray" onClick={clearEntityFilters}>
          {t('orders.clearEntityFilters')}
        </Button>
      ) : null}
    </Stack>
  );

  const filterBanner = hasEntityFilter ? (
    <Alert color="blue" title={t('orders.filteredOrders')}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4}>
          {customerIds.length > 0 ? (
            <Text size="sm">
              {t('orders.filterCustomers', { entity: customerTerms.plural })}{' '}
              {customerIds.map((id) => customerLabels[id] || id).join(', ')}
            </Text>
          ) : null}
          {vendorIds.length > 0 ? (
            <Text size="sm">
              {t('orders.filterVendors', { entity: vendorTerms.plural })}{' '}
              {vendorIds.map((id) => vendorLabels[id] || id).join(', ')}
            </Text>
          ) : null}
          {productIds.length > 0 ? (
            <Text size="sm">
              {t('orders.filterProducts', { entity: productTerms.plural })}{' '}
              {productIds.map((id) => productLabels[id] || id).join(', ')}
            </Text>
          ) : null}
        </Stack>
        <Button size="xs" variant="light" onClick={clearEntityFilters}>
          {t('common.clear')}
        </Button>
      </Group>
    </Alert>
  ) : null;

  const desktopFilters = (
    <Paper p="md" withBorder radius="md" visibleFrom="md">
      <Stack gap="md">
        <Text fw={600} size="sm">
          {t('orders.filterOrders')}
        </Text>
        {entityFilters}
        {hasEntityFilter ? (
          <Button
            variant="light"
            color="gray"
            onClick={clearEntityFilters}
            style={{ alignSelf: 'flex-start' }}
          >
            {t('orders.clearEntityFilters')}
          </Button>
        ) : null}
      </Stack>
    </Paper>
  );

  const resourceLayout = (
    <ResourceLayout
      key={entityFilterKey}
      data={list}
      isLoading={isLoading || (hasEntityFilter && lastAppliedFilterKey !== entityFilterKey)}
      isFetching={isFetching}
      onQueryChange={setQuery}
      searchable={false}
      getRowId={(row) => row.id}
      onRowClick={(row) => navigate(`/orders/${row.id}`)}
      emptyTitle={t('orders.emptyTitle')}
      emptyDescription={t('orders.emptyDescription')}
      onRefresh={() => void refetch()}
      refreshLabel={t('common.refresh')}
      mobileView="list"
      resetKey={`${effectiveListType}-${entityFilterKey}-${query.page}`}
      addButton={
        canCreateActive ? (
          <Button onClick={() => setWizardOpen(true)}>{t('orders.create')}</Button>
        ) : undefined
      }
      filterContent={filterContent}
      filterActiveCount={(tab !== 'all' ? 1 : 0) + entityFilterCount}
      filterOnlyCompact
      columns={[
        {
          key: 'type',
          header: t('orders.colType'),
          render: (row) =>
            row.procType === 'SALES' ? t('orders.sales') : t('orders.purchase'),
        },
        {
          key: 'party',
          header: t('orders.colParty'),
          render: (row) => procPartyName(row, untitled),
        },
        {
          key: 'ord',
          header: t('orders.colOrderNumber'),
          render: (row) => row.ordNumber || empty,
        },
        ...(approvalAccess.procSales || approvalAccess.procPurchase
          ? [
              {
                key: 'approval',
                header: t('orders.approval'),
                render: (row: ProcRow) => {
                  const show =
                    (row.procType === 'SALES' && approvalAccess.procSales) ||
                    (row.procType === 'PURCHASE' && approvalAccess.procPurchase);
                  if (!show) return empty;
                  return (
                    <Badge
                      size="sm"
                      color={approvalBadgeColor(row.approvalStatus)}
                      variant="light"
                    >
                      {row.approvalStatus || t('orders.statusDraft')}
                    </Badge>
                  );
                },
              },
            ]
          : []),
        {
          key: 'amount',
          header: t('orders.colAmount'),
          render: (row) =>
            row.procAmount != null ? `₹${Number(row.procAmount).toFixed(2)}` : empty,
        },
        {
          key: 'createdAt',
          header: t('orders.colDate'),
          render: (row) => format(row.createdAt, 'date') || empty,
        },
      ]}
      renderCard={(row: ProcRow) => (
        <Paper p="md" withBorder radius="md">
          <Stack gap={4}>
            <Text fw={600}>{procPartyName(row, untitled)}</Text>
            <Text size="sm" c="dimmed">
              {row.ordNumber || empty} ·{' '}
              {row.procType === 'SALES' ? t('orders.sales') : t('orders.purchase')}
            </Text>
            {row.procAmount != null && (
              <Text size="sm" fw={700}>
                ₹{Number(row.procAmount).toFixed(2)}
              </Text>
            )}
            {row.createdAt ? (
              <Text size="xs" c="dimmed">
                {format(row.createdAt, 'date')}
              </Text>
            ) : null}
          </Stack>
        </Paper>
      )}
      renderListItem={(row: ProcRow) => {
        const { summary, moreCount } = procProductSummary(row, (name, qty) =>
          t('orders.productSummary', { name, qty }),
        );
        return (
          <ProcurementMobileListRow
            partyName={procPartyName(row, untitled)}
            procType={row.procType}
            amount={row.procAmount}
            createdAt={row.createdAt}
            dateLabel={format(row.createdAt, 'date')}
            timeZone={timeZone}
            productSummary={summary}
            moreProductCount={moreCount}
            address={procAddress(row)}
            onClick={() => navigate(`/orders/${row.id}`)}
          />
        );
      }}
    />
  );

  if (!filterOptions.length) {
    return <NoAccessState onBack={() => navigate('/dashboard')} />;
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={t('orders.title')}
        description={t('orders.description')}
        actions={
          <Group gap="sm">
            <Button
              variant="default"
              disabled={!canCreateActive || !accessToken}
              onClick={() => {
                setImportOpen(true);
                setImportResult(null);
                setImportError(null);
                setImportFile(null);
              }}
            >
              {t('orders.import')}
            </Button>
            <Menu shadow="md" width={220}>
              <Menu.Target>
                <Button variant="default" loading={exporting} disabled={!canMutateActive}>
                  {t('orders.export')}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => void handleExport('EXCEL')}>{t('orders.exportExcel')}</Menu.Item>
                <Menu.Item
                  disabled={!hasApprovedSales}
                  onClick={() => void handleExport('TALLY')}
                >
                  {t('orders.exportTally')}
                  {!hasApprovedSales ? ` ${t('orders.approveFirst')}` : ''}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        }
      />

      {filterBanner}
      {desktopFilters}

      {isError ? (
        <Alert color="red" title={t('orders.loadFailed')}>
          {t('common.connectionError')}
        </Alert>
      ) : null}

      <TabView
        visibleFrom="md"
        value={tab}
        onChange={applyFilter}
        tabs={filterOptions.map((opt) => ({
          value: opt.value,
          title: opt.label,
          content: resourceLayout,
        }))}
      />

      <Stack hiddenFrom="md">{resourceLayout}</Stack>

      <CreateOrderWizard
        opened={wizardOpen}
        onClose={() => setWizardOpen(false)}
        defaultProcType={listType !== 'ALL' ? listType : 'SALES'}
      />

      <Modal
        opened={importOpen}
        onClose={() => setImportOpen(false)}
        title={t('orders.importTitle')}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('orders.importHint')}
          </Text>
          <Group gap="sm">
            <Button
              variant="light"
              loading={templateLoading}
              onClick={() => void handleDownloadTemplate()}
            >
              {t('orders.downloadSample')}
            </Button>
          </Group>
          <input
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => {
              setImportFile(e.target.files?.[0] ?? null);
              setImportResult(null);
              setImportError(null);
            }}
          />
          {importFile ? (
            <Text size="sm">
              {t('orders.selectedFile', { name: importFile.name })}
            </Text>
          ) : null}
          {importError ? <Alert color="red" title={importError} /> : null}
          {importResult ? (
            <Alert
              color={importResult.errors.length ? 'orange' : 'green'}
              title={t('orders.importSummary', {
                imported: importResult.imported,
                parsed: importResult.parsed,
              })}
            >
              {importResult.errors.length ? (
                <Stack gap={4} mt="xs">
                  {importResult.errors.slice(0, 20).map((err) => (
                    <Text key={`${err.row}-${err.message}`} size="xs">
                      {t('orders.importRowError', { row: err.row, message: err.message })}
                    </Text>
                  ))}
                  {importResult.errors.length > 20 ? (
                    <Text size="xs" c="dimmed">
                      {t('orders.importMoreErrors', {
                        count: importResult.errors.length - 20,
                      })}
                    </Text>
                  ) : null}
                </Stack>
              ) : null}
            </Alert>
          ) : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setImportOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={importing}
              disabled={!importFile || !accessToken}
              onClick={() => void handleImportUpload()}
            >
              {t('orders.uploadImport')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
